import { getFlattenedFileNameOverrides } from "../../store/selectors/input/attachments/attachmentsSelectors";
import Disabled from "./Disabled";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MenuIcon,
} from "@mui/icons-material";
import {
  Button,
  Grid,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { commands } from "@uiw/react-md-editor/nohighlight";
import * as logger from "common/logger";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import Markdown from "components/common/Markdown";
import * as commandUtils from "components/common/notes/commandUtils";
import NotesFileSelector from "components/common/notes/commands/Attachment";
import { insertAttachment } from "components/common/notes/commands/Attachment";
import { insertPhoto } from "components/common/notes/commands/Image";
import { link } from "components/common/notes/commands/Link";
import { insert2x2Table as insertTable } from "components/common/notes/commands/Table";
import * as config from "config";
import { useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { connect, useSelector } from "react-redux";
import * as attachmentActions from "store/actions/input/attachments/attachmentsActions";
import * as actions from "store/actions/input/images/imagesActions";
import { isSelectingAttachments } from "store/selectors/temp/attachments/tempAttachmentsSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const Notes = ({
  value,
  update,
  rows,
  disabled,
  uploadImage,
  openAttachmentSelectionDialog,
  closeAttachmentSelectionDialog,
  isSelectingAttachment,
  children,
  header,
  placeholder = "Double-click to edit ...",
  title = "Notes",
  isTitleEditable = false,
  hasEditButton = true,
  growVertically,
  allowAttachments = true,
  allowImages = true,
  attachmentNames,
  onDelete,
  onEnterEditMode,
  onExitEditMode,
  ...componentProps
}) => {
  const toolbarCommands = [
    commandUtils.withTrimmedSelection(commands.bold),
    commandUtils.withTrimmedSelection(commands.italic),
    commandUtils.withTrimmedSelection(commands.strikethrough),
    commands.title,
    commands.divider,
    link,
    commands.quote,
    commands.divider,
    commands.unorderedListCommand,
    commands.orderedListCommand,
    commands.checkedListCommand,
    commands.divider,
    insertTable,
    commands.divider,
    ...(allowAttachments
      ? [insertAttachment(openAttachmentSelectionDialog)]
      : []),
    ...(allowImages ? [insertPhoto(uploadImage)] : []),
  ];

  const [internalTitle, setInternalTitle] = useState(title ?? "");
  const [internalValue, setInternalValue] = useState(value ?? "");

  const [editMode, setEditMode] = useState(false);

  const isUserReadOnly = useSelector(userSelectors.isUserReadOnly);

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const enterEditMode = () => {
    if (editMode) {
      return;
    }
    if (isUserReadOnly) {
      return;
    }
    setInternalTitle(title);
    setInternalValue(value);
    if (!disabled) {
      setEditMode(true);
      onEnterEditMode && onEnterEditMode();
    }
  };

  const exitEditMode = async () => {
    if (isSelectingAttachment) {
      closeAttachmentSelectionDialog();
    } else {
      if (update) {
        try {
          await update(internalValue, internalTitle);
        } catch (e) {
          logger.exception(e);
          return;
        }
      }
      setEditMode(false);
      onExitEditMode && onExitEditMode();
    }
  };

  const attachmentSelected = (meta) => {
    let viewName = meta.originalFilename;
    const params = {
      fileId: meta.id,
      mime: meta.mimeType,
    };
    const override = attachmentNames[meta.id];
    if (override) {
      params["filename"] = override;
      viewName = override;
    }

    const url = "/attachments/fileView?" + new URLSearchParams(params);
    setInternalValue(internalValue + "\n[" + viewName + "](" + url + ")\n");
    closeAttachmentSelectionDialog();
  };

  return (
    <Component
      title={title}
      {...componentProps}
      options={
        <Stack direction={"row"} spacing={1}>
          {!disabled &&
            hasEditButton &&
            (editMode ? (
              <Button
                disableElevation
                variant={"contained"}
                key={"disabled"}
                sx={{ height: "36px", margin: "2px 0" }}
              >
                {isSelectingAttachment ? "Cancel" : "Done"}
              </Button>
            ) : (
              <Disabled ifReadOnly>
                <IconButton
                  variant={"bright"}
                  icon={EditIcon}
                  onClick={enterEditMode}
                />
              </Disabled>
            ))}
          {!disabled && onDelete && !editMode && (
            <>
              <IconButton icon={MenuIcon} onClick={(e) => openMenu(e)} />
              <Menu
                anchorEl={anchorEl}
                open={anchorEl != null}
                onClose={(_, reason) => {
                  if (reason === "backdropClick") closeMenu();
                }}
              >
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onDelete();
                  }}
                  sx={{ minWidth: "12rem" }}
                >
                  <ListItemText primary={"Delete"} />
                  <ListItemIcon>
                    <DeleteIcon />
                  </ListItemIcon>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      }
      onDoubleClick={enterEditMode}
      compact
      growVertically
    >
      {(editMode || value || !!children || !!header || placeholder) && (
        <Grid container spacing={config.GRID_SPACING} direction={"row"}>
          {!!header && (
            <Grid item xs={12}>
              {header}
            </Grid>
          )}
          {editMode ? (
            <Grid item xs={12} data-testid={"notes-editor"}>
              <OutsideClickHandler
                onOutsideClick={exitEditMode}
                display={"contents"}
              >
                <Stack direction={"column"} gap={1} sx={{ width: "100%" }}>
                  {isTitleEditable && !isSelectingAttachment && (
                    <TextField
                      variant={"outlined"}
                      size={"small"}
                      fullWidth
                      value={internalTitle}
                      onChange={(e) => setInternalTitle(e.target.value ?? "")}
                    />
                  )}
                  {isSelectingAttachment ? (
                    <NotesFileSelector onSelected={attachmentSelected} />
                  ) : (
                    <Markdown
                      editMode={true}
                      value={internalValue}
                      onChange={setInternalValue}
                      commands={toolbarCommands}
                    />
                  )}
                </Stack>
              </OutsideClickHandler>
            </Grid>
          ) : value ? (
            <Grid item xs={12} data-testid={"notes-view"}>
              <Markdown
                editMode={false}
                value={value}
                commands={toolbarCommands}
              />
            </Grid>
          ) : placeholder ? (
            <Grid
              item
              xs={12}
              sx={{
                color: "black",
                fontSize: "small",
                fontWeight: "bold",
                opacity: 0.2,
              }}
              data-testid={"empty-note"}
            >
              {placeholder}
            </Grid>
          ) : (
            <></>
          )}

          {!!children && (
            <Grid item xs={12}>
              {children}
            </Grid>
          )}
        </Grid>
      )}
    </Component>
  );
};

const mapDispatchToProps = {
  uploadImage: actions.uploadImage,
  openAttachmentSelectionDialog:
    attachmentActions.openAttachmentSelectionDialog,
  closeAttachmentSelectionDialog:
    attachmentActions.closeAttachmentSelectionDialog,
};

const mapStateToProps = (state) => ({
  isSelectingAttachment: isSelectingAttachments(state),
  attachmentNames: getFlattenedFileNameOverrides(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notes);
